// Thumbnails for uploaded content. The browser renders them — GLB/GLTF
// offscreen with three.js (camera fitted to the bounds, neutral studio light),
// images downscaled, video a frame grab — and /api/thumbnail stores the JPG in
// R2 under thumbnails/ and sets thumbnail_url on the row.
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { getDRACOLoader, MeshoptDecoder } from './loaderConfig';
import { gathrxrAuthHeader } from './quotaHeaders';

const SIZE = 512;
const BG = '#0b1624';
/** GLBs bigger than this are skipped by the automatic pass (they'd stall the tab). */
export const AUTO_THUMB_MAX_BYTES = 150 * 1024 * 1024;

export type ThumbKind = 'model' | 'image' | 'video' | 'none';

export function thumbKindFor(nameOrUrl: string, mime?: string | null): ThumbKind {
  const ext = (nameOrUrl.split('?')[0].split('.').pop() || '').toLowerCase();
  if (ext === 'glb' || ext === 'gltf' || mime?.startsWith('model/')) return 'model';
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'avif'].includes(ext) || /^image\/(jpeg|png|webp|gif|avif)/.test(mime || '')) return 'image';
  if (['mp4', 'webm', 'mov', 'm4v'].includes(ext) || mime?.startsWith('video/')) return 'video';
  return 'none';
}

let renderer: THREE.WebGLRenderer | null = null;
function getRenderer() {
  if (!renderer) {
    renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true, alpha: false });
    renderer.setPixelRatio(1);
    renderer.setSize(SIZE, SIZE, false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
  }
  return renderer;
}

function canvasToJpeg(canvas: HTMLCanvasElement, quality = 0.85): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not encode thumbnail'))), 'image/jpeg', quality));
}

function disposeTree(root: THREE.Object3D) {
  root.traverse((o: any) => {
    o.geometry?.dispose?.();
    const mats = Array.isArray(o.material) ? o.material : o.material ? [o.material] : [];
    for (const m of mats) {
      for (const v of Object.values(m)) if (v && (v as any).isTexture) (v as THREE.Texture).dispose();
      m.dispose?.();
    }
  });
}

/** Render a GLB/GLTF (URL or File) to a square JPG. */
export async function renderModelThumbnail(src: string | File | Blob): Promise<Blob> {
  const loader = new GLTFLoader();
  loader.setDRACOLoader(getDRACOLoader());
  loader.setMeshoptDecoder(MeshoptDecoder);
  let objectUrl: string | null = null;
  const url = typeof src === 'string' ? src : (objectUrl = URL.createObjectURL(src));
  try {
    const gltf = await loader.loadAsync(url);
    const model = gltf.scene;
    // Physics-only collision boxes and invisible seat markers aren't part of the look
    model.traverse((o: any) => {
      if (o.isMesh && /^(collider|ucx|collision)[_.]/i.test(o.name || '')) o.visible = false;
    });
    model.updateMatrixWorld(true);
    const box = new THREE.Box3();
    model.traverse((o: any) => { if (o.isMesh && o.visible) box.expandByObject(o); });
    if (box.isEmpty()) box.setFromObject(model);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(BG);
    scene.add(new THREE.HemisphereLight(0xffffff, 0x334455, 1.6));
    const key = new THREE.DirectionalLight(0xffffff, 2.2);
    key.position.set(3, 5, 4);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.7);
    fill.position.set(-4, 2, -3);
    scene.add(fill);
    scene.add(model);

    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const radius = Math.max(size.length() / 2, 1e-3);
    const camera = new THREE.PerspectiveCamera(35, 1, radius / 100, radius * 100);
    const dist = radius / Math.sin(THREE.MathUtils.degToRad(35 / 2)) * 1.02;
    // Three-quarter view from front-right, a little above
    const dir = new THREE.Vector3(0.75, 0.5, 1).normalize();
    camera.position.copy(center).addScaledVector(dir, dist);
    camera.lookAt(center);

    const r = getRenderer();
    r.render(scene, camera);
    const blob = await canvasToJpeg(r.domElement);
    scene.remove(model);
    disposeTree(model);
    return blob;
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}

function drawCover(source: CanvasImageSource, w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = SIZE; c.height = SIZE;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, SIZE, SIZE);
  const s = Math.max(SIZE / w, SIZE / h);
  ctx.drawImage(source, (SIZE - w * s) / 2, (SIZE - h * s) / 2, w * s, h * s);
  return c;
}

/** Downscale an image (URL or File) to a square JPG. */
export async function renderImageThumbnail(src: string | File | Blob): Promise<Blob> {
  let objectUrl: string | null = null;
  const url = typeof src === 'string' ? src : (objectUrl = URL.createObjectURL(src));
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    await new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = () => rej(new Error('Image failed to load')); img.src = url; });
    return await canvasToJpeg(drawCover(img, img.naturalWidth, img.naturalHeight));
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}

/** Grab a frame ~1s in (or 10% for short clips) as a square JPG. */
export async function renderVideoThumbnail(src: string | File | Blob): Promise<Blob> {
  let objectUrl: string | null = null;
  const url = typeof src === 'string' ? src : (objectUrl = URL.createObjectURL(src));
  try {
    const v = document.createElement('video');
    v.crossOrigin = 'anonymous';
    v.muted = true;
    v.playsInline = true;
    v.preload = 'auto';
    v.src = url;
    await new Promise<void>((res, rej) => { v.onloadedmetadata = () => res(); v.onerror = () => rej(new Error('Video failed to load')); });
    v.currentTime = Math.min(1, (v.duration || 1) * 0.1);
    await new Promise<void>((res, rej) => { v.onseeked = () => res(); v.onerror = () => rej(new Error('Video seek failed')); });
    return await canvasToJpeg(drawCover(v, v.videoWidth, v.videoHeight));
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}

export async function renderThumbnail(src: string | File | Blob, kind: ThumbKind): Promise<Blob | null> {
  if (kind === 'model') return renderModelThumbnail(src);
  if (kind === 'image') return renderImageThumbnail(src);
  if (kind === 'video') return renderVideoThumbnail(src);
  return null;
}

/** Store a rendered thumbnail for a row; returns its public URL. */
export async function uploadThumbnail(table: 'user_uploads' | 'library_assets', id: string, blob: Blob): Promise<string> {
  const res = await fetch(`/api/thumbnail?table=${table}&id=${encodeURIComponent(id)}`, {
    method: 'POST',
    headers: { ...(await gathrxrAuthHeader()), 'Content-Type': blob.type || 'image/jpeg' },
    body: blob,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `Thumbnail upload failed (${res.status})`);
  return body.url as string;
}

/** Render + store in one go. Returns the URL, or null for kinds with no thumbnail (audio, splats). */
export async function makeThumbnailFor(
  table: 'user_uploads' | 'library_assets', id: string, src: string | File | Blob, nameOrUrl: string, mime?: string | null,
): Promise<string | null> {
  const kind = thumbKindFor(nameOrUrl, mime);
  const blob = await renderThumbnail(src, kind);
  if (!blob) return null;
  return uploadThumbnail(table, id, blob);
}
